import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Phone, MessageCircle, Calendar, User, Mail, Clock, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import PinAuth from '../components/PinAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, contacted
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const savedPin = sessionStorage.getItem('adminPin');
    if (savedPin) {
      // Verify PIN with backend
      axios.get(`${API}/admin/pin/verify?pin=${savedPin}`)
        .then(response => {
          if (response.data.valid) {
            setIsAuthenticated(true);
          }
        })
        .catch(() => {
          sessionStorage.removeItem('adminPin');
        });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadAppointments();
      
      // Auto-refresh every 10 seconds
      const interval = setInterval(() => {
        loadAppointments();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Failed to load appointments');
    }
  };

  const handleCall = async (appointment) => {
    // Mark as contacted
    try {
      await axios.patch(`${API}/appointments/${appointment.id}`, {
        ...appointment,
        status: 'contacted'
      });
      toast.success('Appointment marked as contacted');
      loadAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
    
    // Call the PATIENT
    window.location.href = `tel:${appointment.phone}`;
  };

  const handleWhatsApp = async (appointment) => {
    // Mark as contacted
    try {
      await axios.patch(`${API}/appointments/${appointment.id}`, {
        ...appointment,
        status: 'contacted'
      });
      toast.success('Appointment marked as contacted');
      loadAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
    
    // Open WhatsApp to the PATIENT
    const message = `Hi ${appointment.name}, this is Dard Satya team. We received your consultation request for ${appointment.appointmentType}. How can we help you?`;
    window.open(`https://wa.me/91${appointment.phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPin');
    setIsAuthenticated(false);
  };

  const filteredAppointments = appointments.filter(appt => {
    if (filter === 'pending') return appt.status === 'pending';
    if (filter === 'contacted') return appt.status === 'contacted';
    return true;
  });

  // PIN Login Screen
  if (!isAuthenticated) {
    return <PinAuth onSuccess={() => setIsAuthenticated(true)} title="Admin Appointments" icon={Calendar} />;
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 1000 / 60);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="admin-appointments-page min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Consultation Requests</h1>
              <p className="text-gray-600">Manage appointment and consultation requests</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Requests</p>
                  <p className="text-3xl font-bold text-gray-900">{appointments.length}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <Calendar className="text-blue-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Pending</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {appointments.filter(a => a.status === 'pending').length}
                  </p>
                </div>
                <div className="bg-orange-100 p-3 rounded-full">
                  <Bell className="text-orange-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Contacted</p>
                  <p className="text-3xl font-bold text-green-600">
                    {appointments.filter(a => a.status === 'contacted').length}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <Clock className="text-green-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-600'}
          >
            All ({appointments.length})
          </Button>
          <Button
            onClick={() => setFilter('pending')}
            variant={filter === 'pending' ? 'default' : 'outline'}
            className={filter === 'pending' ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-600 text-orange-600'}
          >
            Pending ({appointments.filter(a => a.status === 'pending').length})
          </Button>
          <Button
            onClick={() => setFilter('contacted')}
            variant={filter === 'contacted' ? 'default' : 'outline'}
            className={filter === 'contacted' ? 'bg-green-600 hover:bg-green-700' : 'border-green-600 text-green-600'}
          >
            Contacted ({appointments.filter(a => a.status === 'contacted').length})
          </Button>
        </div>

        {/* Appointments List */}
        <div className="space-y-4">
          {filteredAppointments.length === 0 ? (
            <Card className="border-none shadow-lg">
              <CardContent className="p-12 text-center">
                <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 text-lg">No consultation requests yet</p>
                <p className="text-gray-500 text-sm mt-2">
                  Requests will appear here when patients book consultations
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredAppointments.map((appt) => (
              <Card 
                key={appt.id} 
                className={`border-l-4 shadow-md hover:shadow-lg transition-all ${
                  appt.status === 'pending' 
                    ? 'border-l-orange-600 bg-orange-50/30' 
                    : 'border-l-green-600'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge 
                          className={`${
                            appt.status === 'pending' 
                              ? 'bg-orange-600 hover:bg-orange-700' 
                              : 'bg-green-600 hover:bg-green-700'
                          } text-white`}
                        >
                          {appt.status}
                        </Badge>
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <Clock size={14} />
                          {formatTimestamp(appt.timestamp)}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={16} className="text-blue-600" />
                            <p className="text-sm text-gray-600">Patient Name</p>
                          </div>
                          <p className="text-lg font-bold text-gray-900">{appt.name}</p>
                        </div>
                        
                        <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <Phone size={16} className="text-green-600" />
                            <p className="text-sm text-gray-600">Phone Number</p>
                          </div>
                          <p className="text-lg font-bold text-gray-900">{appt.phone}</p>
                        </div>
                      </div>
                      
                      {appt.email && (
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Mail size={16} className="text-purple-600" />
                            <p className="text-sm text-gray-600">Email</p>
                          </div>
                          <p className="text-gray-900">{appt.email}</p>
                        </div>
                      )}
                      
                      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-3">
                        <p className="text-sm text-gray-600 mb-1">Consultation Type</p>
                        <p className="font-semibold text-gray-900 mb-3">{appt.appointmentType}</p>
                        
                        {appt.preferredDate && (
                          <>
                            <p className="text-sm text-gray-600 mb-1">Preferred Date</p>
                            <p className="text-gray-900 mb-3">{new Date(appt.preferredDate).toLocaleDateString()}</p>
                          </>
                        )}
                        
                        {appt.message && (
                          <>
                            <p className="text-sm text-gray-600 mb-1">Message</p>
                            <p className="text-gray-700">{appt.message}</p>
                          </>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                          ID: {appt.id.substring(0, 8)}...
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex md:flex-col gap-2">
                      <Button 
                        onClick={() => handleCall(appt)}
                        className="bg-blue-600 hover:bg-blue-700 text-white w-full md:w-auto"
                      >
                        <Phone size={16} className="mr-2" />
                        Call Patient
                      </Button>
                      <Button 
                        onClick={() => handleWhatsApp(appt)}
                        variant="outline" 
                        className="border-green-600 text-green-600 hover:bg-green-50 w-full md:w-auto"
                      >
                        <MessageCircle size={16} className="mr-2" />
                        WhatsApp
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Instructions */}
        <Card className="mt-8 border-none shadow-lg bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 How to Access This Page</h3>
            <p className="text-gray-700 mb-4">
              Save this URL to check consultation requests. This page auto-refreshes every 10 seconds.
            </p>
            <div className="bg-white p-4 rounded-lg border-2 border-blue-300 font-mono text-sm">
              {window.location.origin}/admin/appointments
            </div>
            <p className="text-sm text-gray-600 mt-3">
              💡 Tip: New appointment requests will be sent to admin WhatsApp 9425025425
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAppointments;
